import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { fmtMoney } from "../lib/format";
import {
  Plane, Train, Hotel as HotelIcon, Trophy, ArrowRight, Sparkles,
  Save, Calendar as CalendarIcon, Loader2, ExternalLink, Info,
  CheckCircle2, AlertTriangle, Brain, X,
} from "lucide-react";

const STRATEGY_META = {
  budget:   { label: "Budget",   tone: "emerald", desc: "Minimum euros · trains preferred · cheap hotels" },
  coverage: { label: "Coverage", tone: "indigo",  desc: "See every selected client · flights ok · accept higher cost" },
  speed:    { label: "Speed",    tone: "rose",    desc: "Fewest days · flights · skip low-priority matches" },
};

const TONE = {
  emerald: { ring: "border-emerald-300", chip: "bg-emerald-50 text-emerald-700", btn: "bg-emerald-600 hover:bg-emerald-700" },
  indigo:  { ring: "border-indigo-300",  chip: "bg-indigo-50 text-indigo-700",   btn: "bg-indigo-600 hover:bg-indigo-700" },
  rose:    { ring: "border-rose-300",    chip: "bg-rose-50 text-rose-700",       btn: "bg-rose-600 hover:bg-rose-700" },
};

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

function fmtClock(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

function legKey(strategyIdx, legIdx) { return `${strategyIdx}:${legIdx}`; }

export default function PlanFromSelectionPanel({ planResult, onClear, onToast }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [enriched, setEnriched] = useState({});  // {legKey: {kind, status, data, fetched_at}}
  const [savingIdx, setSavingIdx] = useState(null);
  const [calendaring, setCalendaring] = useState(null);

  const options = planResult?.options || [];
  const active = options[activeIdx];

  // Auto-enrich when active option changes
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    (async () => {
      const tasks = (active.legs || []).map((leg, idx) => {
        const k = legKey(activeIdx, idx);
        if (enriched[k] && enriched[k].status === "ok") return null;
        return enrichLeg(leg, k, cancelled);
      });
      await Promise.all(tasks.filter(Boolean));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx, active?.legs?.length]);

  async function enrichLeg(leg, k, cancelled) {
    setEnriched((prev) => ({ ...prev, [k]: { kind: leg.kind, status: "loading" } }));
    try {
      let data = null;
      if (leg.kind === "flight") {
        const { data: r } = await api.post("/travel/enrich/flight", {
          from_city: leg.from_city, to_city: leg.to_city, date: leg.date, adults: 1,
        });
        data = r;
      } else if (leg.kind === "train") {
        const { data: r } = await api.post("/travel/enrich/train", {
          from_city: leg.from_city, to_city: leg.to_city, date: leg.date,
        });
        data = r;
      } else if (leg.kind === "hotel") {
        const { data: r } = await api.post("/travel/enrich/hotel", {
          city: leg.city, checkin: leg.checkin || leg.date,
          checkout: leg.checkout || leg.date,
        });
        data = r;
      } else {
        data = null;
      }
      if (!cancelled) {
        setEnriched((prev) => ({
          ...prev,
          [k]: { kind: leg.kind, status: "ok", data, fetched_at: data?.fetched_at || new Date().toISOString() },
        }));
      }
    } catch (e) {
      if (!cancelled) {
        setEnriched((prev) => ({ ...prev, [k]: { kind: leg.kind, status: "error", error: e?.message || "fetch failed" } }));
      }
    }
  }

  const totalPriceEur = useMemo(() => {
    if (!active) return 0;
    let sum = 0;
    (active.legs || []).forEach((leg, idx) => {
      const enr = enriched[legKey(activeIdx, idx)];
      if (!enr || enr.status !== "ok") return;
      if (leg.kind === "flight") {
        const cheapest = (enr.data?.results || [])[0];
        if (cheapest?.price_eur) sum += cheapest.price_eur;
      } else if (leg.kind === "train") {
        if (enr.data?.price_eur) sum += enr.data.price_eur;
      } else if (leg.kind === "hotel") {
        const cheapest = (enr.data?.results || [])[0];
        const nights = leg.nights || 1;
        if (cheapest?.price_eur) sum += cheapest.price_eur * nights;
      }
    });
    return Math.round(sum);
  }, [active, enriched, activeIdx]);

  const hasAnyLive = useMemo(() => {
    if (!active) return false;
    return (active.legs || []).some((_, idx) => {
      const enr = enriched[legKey(activeIdx, idx)];
      return enr?.status === "ok" && enr.data?.live === true;
    });
  }, [active, enriched, activeIdx]);

  async function saveItinerary() {
    if (!active) return;
    setSavingIdx(activeIdx);
    try {
      // Materialize legs with enrichment snapshot
      const materialized = (active.legs || []).map((leg, idx) => {
        const enr = enriched[legKey(activeIdx, idx)];
        return { ...leg, enrichment: enr?.data || null };
      });
      const { data } = await api.post("/travel-planner/itineraries", {
        name: active.name || `${(active.strategy || "trip")} trip`,
        strategy: active.strategy || "budget",
        home_city: planResult.home_city || "Amsterdam",
        start_date: active.start_date || "",
        end_date: active.end_date || "",
        legs: materialized,
        total_cost_eur: totalPriceEur || active.estimated_total_eur || 0,
        notes: active.reasoning || "",
        fixture_ids: planResult.fixture_ids || [],
      });
      onToast && onToast(`Itinerary saved · ${data?.itinerary?.id}`);
      return data?.itinerary;
    } catch (e) {
      onToast && onToast("Could not save itinerary");
      return null;
    } finally {
      setSavingIdx(null);
    }
  }

  async function addToCalendar() {
    if (!active) return;
    setCalendaring(activeIdx);
    try {
      const trip = await saveItinerary();
      if (!trip?.id) return;
      const { data } = await api.post(`/travel-planner/itineraries/${trip.id}/push-calendar`, { provider: "auto" });
      // Always offer .ics download as a guaranteed fallback
      if (data?.ics_url) {
        const win = window.open(data.ics_url, "_blank");
        if (!win) onToast && onToast(".ics ready — check downloads");
      }
      if (data?.target === "outlook" || data?.target === "gmail") {
        onToast && onToast(`${data.pushed} events pushed to ${data.target}`);
      } else {
        onToast && onToast(".ics file downloaded · import to any calendar");
      }
    } catch (e) {
      onToast && onToast("Calendar push failed");
    } finally {
      setCalendaring(null);
    }
  }

  if (!planResult || !options.length) return null;

  return (
    <section
      className="space-y-4"
      data-testid="plan-from-selection-panel"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 px-2.5 h-7 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 text-[11px] font-bold">
          <Sparkles size={12} /> AI-planned · {(planResult.fixture_ids || []).length} matches selected
        </div>
        {planResult.ai_used ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 font-bold">
            <Brain size={12} className="text-emerald-700" /> Built by Claude Sonnet 4.5
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 font-bold">
            <AlertTriangle size={12} /> AI fallback (deterministic) · Claude unavailable
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {options.map((o, i) => {
            const meta = STRATEGY_META[o.strategy] || STRATEGY_META.budget;
            const t = TONE[meta.tone];
            return (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                data-testid={`plan-option-tab-${o.strategy || i}`}
                className={`h-9 px-3 rounded-lg text-xs font-bold border-2 transition-all ${i === activeIdx ? `${t.ring} ${t.chip}` : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}
              >
                {meta.label}
              </button>
            );
          })}
          <button
            onClick={onClear}
            data-testid="plan-clear-btn"
            className="h-9 px-3 rounded-lg text-xs font-bold border-2 border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700 inline-flex items-center gap-1"
            title="Clear plan and start over"
          >
            <X size={12} /> Clear
          </button>
        </div>
      </div>

      {active && (
        <div className={`pm-card overflow-hidden border-2 ${TONE[STRATEGY_META[active.strategy]?.tone || "emerald"].ring}`} data-testid={`plan-card-${active.strategy}`}>
          <div className="p-5 flex items-start gap-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-transparent">
            <div className={`w-11 h-11 rounded-xl ${TONE[STRATEGY_META[active.strategy]?.tone || "emerald"].chip} flex items-center justify-center`}>
              <Plane size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-2xl font-bold tracking-tight" data-testid="plan-trip-title">
                {active.name || `${STRATEGY_META[active.strategy]?.label || "Trip"} option`}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {active.start_date} → {active.end_date} · {STRATEGY_META[active.strategy]?.desc || ""}
              </div>
              {active.reasoning && (
                <div className="text-[11px] text-slate-600 italic mt-1 flex items-start gap-1">
                  <Sparkles size={10} className="text-emerald-600 mt-0.5 shrink-0" /> {active.reasoning}
                </div>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className="font-display text-3xl font-black text-slate-900 leading-none">
                {fmtMoney(totalPriceEur || active.estimated_total_eur || 0)}
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 mt-1">
                {totalPriceEur ? "Live total" : "Estimated"}
              </div>
            </div>
          </div>

          {/* Legs timeline */}
          <div className="px-5 py-4 space-y-2" data-testid="plan-legs">
            {(active.legs || []).map((leg, idx) => (
              <LegRow
                key={`${active.strategy}-${idx}`}
                leg={leg}
                state={enriched[legKey(activeIdx, idx)]}
                onRetry={() => enrichLeg(leg, legKey(activeIdx, idx), false)}
                idx={idx}
              />
            ))}
          </div>

          {/* Affiliate disclosure */}
          <div className="px-5 py-3 bg-amber-50/40 border-t border-amber-200/60 text-[11px] text-amber-900 flex items-start gap-2" data-testid="affiliate-disclosure">
            <Info size={12} className="mt-0.5 shrink-0" />
            <span>
              <b>Affiliate disclosure:</b> Prices are sourced live from Google Flights via Apify. Booking buttons open Google Flights / Booking.com / Omio (pre-filled, ready to book) via our affiliate links.
              We may earn a referral fee at no additional cost to you. {hasAnyLive ? "Live prices fetched from provider APIs." : "Showing fallback estimates — live prices appear once the Apify Google Flights run completes."}
            </span>
          </div>

          <div className="px-5 py-4 border-t border-slate-100 flex flex-wrap items-center gap-2 bg-slate-50/60">
            <span className="text-[11px] text-slate-500">
              {(active.legs || []).length} legs · {hasAnyLive ? <span className="text-emerald-700 font-bold">Real prices loaded</span> : <span className="text-slate-500">Fallback prices</span>}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={saveItinerary}
                disabled={savingIdx !== null}
                data-testid="plan-save-itinerary-btn"
                className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
              >
                {savingIdx === activeIdx ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Save itinerary
              </button>
              <button
                onClick={addToCalendar}
                disabled={calendaring !== null}
                data-testid="plan-add-to-calendar-btn"
                className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
              >
                {calendaring === activeIdx ? <Loader2 size={13} className="animate-spin" /> : <CalendarIcon size={13} />}
                Add to calendar
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function LegRow({ leg, state, onRetry, idx }) {
  const status = state?.status || "loading";
  const data = state?.data;
  const fetchedAt = data?.fetched_at;

  if (leg.kind === "match") {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50/30" data-testid={`leg-${idx}-match`}>
        <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><Trophy size={14} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm truncate">
            {leg.home_team || "Match"} {leg.away_team ? `vs ${leg.away_team}` : ""}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            {leg.client_name ? <b>{leg.client_name}</b> : null}
            {leg.competition ? <span> · {leg.competition}</span> : null}
            {leg.city ? <span> · {leg.city}</span> : null}
          </div>
        </div>
        <div className="text-right font-mono-tab text-xs text-slate-500 shrink-0">
          <div>{(leg.date || "").slice(0, 10)}</div>
          <div className="font-bold text-slate-900">{fmtClock(leg.date)}</div>
        </div>
      </div>
    );
  }

  if (leg.kind === "flight") {
    const results = data?.results || [];
    const cheapest = results[0];
    if (status === "loading") {
      return <LoadingLeg label={`Flight ${leg.from_city} → ${leg.to_city} · ${leg.date}`} icon={Plane} idx={idx} kind="flight" />;
    }
    if (status === "error" || !cheapest) {
      return <ErrorLeg label={`Flight ${leg.from_city} → ${leg.to_city}`} icon={Plane} idx={idx} kind="flight"
                onRetry={onRetry}
                manualUrl={`https://www.google.com/travel/flights?q=${encodeURIComponent(`Flights to ${leg.to_iata || leg.to_city} from ${leg.from_iata || leg.from_city} on ${leg.date}`)}&curr=EUR&hl=en`}
                manualLabel="Search on Google Flights" />;
    }
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50/30" data-testid={`leg-${idx}-flight`}>
        {cheapest.airline_logo ? (
          <img src={cheapest.airline_logo} alt={cheapest.airline} className="w-8 h-8 rounded-lg object-contain bg-white border border-indigo-100" />
        ) : (
          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-700"><Plane size={14} /></div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm flex items-center gap-1.5 truncate">
            {leg.from_city} <ArrowRight size={12} className="text-slate-400" /> {leg.to_city}
            <span className="text-indigo-700 text-xs font-bold ml-2">
              {cheapest.airline} · {cheapest.duration_min ? `${Math.floor(cheapest.duration_min/60)}h${cheapest.duration_min%60}m` : "—"} · {fmtMoney(cheapest.price_eur)}
            </span>
            {cheapest.stops > 0 && <span className="text-[10px] text-slate-500 ml-1">{cheapest.stops} stop{cheapest.stops > 1 ? "s" : ""}</span>}
            {data?.live ? (
              <span className="pm-pill pm-pill-green text-[10px] ml-1">Live</span>
            ) : (
              <span className="pm-pill pm-pill-amber text-[10px] ml-1">Estimate</span>
            )}
            {results.length > 1 && <span className="text-[10px] text-slate-500 ml-1">+{results.length - 1} more</span>}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
            <span>{leg.date}</span>
            {cheapest.carbon_emissions_kg && <span>· {Math.round(cheapest.carbon_emissions_kg)}kg CO₂</span>}
            {fetchedAt && <span>· prices as of <b>{fmtTime(fetchedAt)}</b></span>}
          </div>
        </div>
        <a
          href={cheapest.book_url} target="_blank" rel="noopener noreferrer"
          onClick={() => api.post("/travel/affiliate-click", { provider: data?.provider || "skyscanner", stop_type: "flight", estimated_value_eur: cheapest.price_eur }).catch(() => {})}
          className="pm-btn-primary h-8 px-3 rounded-lg text-xs font-bold inline-flex items-center gap-1"
          data-testid={`leg-${idx}-book-flight`}
        >Search on Google Flights <ExternalLink size={11} /></a>
      </div>
    );
  }

  if (leg.kind === "train") {
    if (status === "loading") return <LoadingLeg label={`Train ${leg.from_city} → ${leg.to_city}`} icon={Train} idx={idx} kind="train" />;
    if (status === "error" || !data) {
      return <ErrorLeg label={`Train ${leg.from_city} → ${leg.to_city}`} icon={Train} idx={idx} kind="train"
                onRetry={onRetry}
                manualUrl={`https://www.omio.com/results/train?origin=${encodeURIComponent(leg.from_city || "")}&destination=${encodeURIComponent(leg.to_city || "")}&outward_date=${leg.date}`}
                manualLabel="Search Omio manually" />;
    }
    if (!data.rail_recommended) {
      // Treat as flight fallback rendering note
      return (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white" data-testid={`leg-${idx}-train`}>
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700"><Train size={14} /></div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm flex items-center gap-1.5 truncate">
              {leg.from_city} <ArrowRight size={12} className="text-slate-400" /> {leg.to_city}
              <span className="text-slate-500 text-xs ml-2">No direct rail · check flights</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">{leg.date}</div>
          </div>
          <a href={data.book_url} target="_blank" rel="noopener noreferrer"
             className="pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold inline-flex items-center gap-1"
             data-testid={`leg-${idx}-book-train`}>
            Search Omio <ExternalLink size={11} />
          </a>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-emerald-300 bg-emerald-50/50" data-testid={`leg-${idx}-train`}>
        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700"><Train size={14} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm flex items-center gap-1.5 truncate">
            {leg.from_city} <ArrowRight size={12} className="text-slate-400" /> {leg.to_city}
            <span className="text-emerald-700 text-xs font-bold ml-2">
              {data.train_name} · {data.duration_min ? `${Math.floor(data.duration_min/60)}h${data.duration_min%60}m` : "—"} · {fmtMoney(data.price_eur)}
            </span>
            <span className="pm-pill pm-pill-green text-[10px] ml-1">Rail recommended</span>
          </div>
          <div className="text-[11px] text-emerald-700 mt-0.5">{data.reason}</div>
        </div>
        <a href={data.book_url} target="_blank" rel="noopener noreferrer"
           onClick={() => api.post("/travel/affiliate-click", { provider: "omio", stop_type: "train", estimated_value_eur: data.price_eur }).catch(() => {})}
           className="pm-btn-primary h-8 px-3 rounded-lg text-xs font-bold inline-flex items-center gap-1"
           data-testid={`leg-${idx}-book-train`}>
          Book · Omio <ExternalLink size={11} />
        </a>
      </div>
    );
  }

  if (leg.kind === "hotel") {
    const results = data?.results || [];
    if (status === "loading") return <LoadingLeg label={`Hotel · ${leg.city}`} icon={HotelIcon} idx={idx} kind="hotel" />;
    if (status === "error" || !results.length) {
      return <ErrorLeg label={`Hotel · ${leg.city}`} icon={HotelIcon} idx={idx} kind="hotel"
                onRetry={onRetry}
                manualUrl={`https://www.booking.com/searchresults.html?ss=${encodeURIComponent(leg.city || "")}&checkin=${leg.checkin || leg.date}&checkout=${leg.checkout || leg.date}`}
                manualLabel="Search Booking.com manually" />;
    }
    const opts = results.slice(0, 3);
    return (
      <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-2" data-testid={`leg-${idx}-hotel`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center text-violet-700"><HotelIcon size={14} /></div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">Overnight · {leg.city} · {leg.checkin || leg.date}</div>
            <div className="text-[11px] text-slate-500">
              {opts.length} options · {data?.live ? <b className="text-emerald-700">Live Booking.com</b> : "Estimate"}
              {fetchedAt && <span> · prices as of <b>{fmtTime(fetchedAt)}</b></span>}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pl-11">
          {opts.map((h, i) => (
            <a key={i} href={h.book_url} target="_blank" rel="noopener noreferrer"
               onClick={() => api.post("/travel/affiliate-click", { provider: "booking_com", stop_type: "hotel", estimated_value_eur: h.price_eur }).catch(() => {})}
               className="block bg-white border border-violet-200 hover:border-violet-400 rounded-lg p-2 transition-colors"
               data-testid={`leg-${idx}-hotel-option-${i}`}>
              <div className="font-bold text-xs truncate">{h.name}</div>
              <div className="text-[10px] text-slate-500 truncate">
                {"★".repeat(h.stars || 3)}
                {h.review_score ? ` · ${h.review_score?.toFixed(1)}/10` : ""}
                {h.free_cancellation ? " · Free cancel" : ""}
              </div>
              <div className="font-bold text-sm text-violet-700 mt-1">{fmtMoney(h.price_eur)}/night</div>
            </a>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

function LoadingLeg({ label, icon: Icon, idx, kind }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white animate-pulse" data-testid={`leg-${idx}-${kind}-loading`}>
      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400"><Icon size={14} /></div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-slate-500 truncate">{label}</div>
        <div className="text-[11px] text-slate-400">Fetching live prices…</div>
      </div>
      <Loader2 size={14} className="animate-spin text-slate-400" />
    </div>
  );
}

function ErrorLeg({ label, icon: Icon, idx, kind, onRetry, manualUrl, manualLabel }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50/40" data-testid={`leg-${idx}-${kind}-error`}>
      <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><Icon size={14} /></div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-amber-900 truncate">{label}</div>
        <div className="text-[11px] text-amber-700">Live prices unavailable — try the manual search below.</div>
      </div>
      <button onClick={onRetry} className="text-[11px] font-bold text-amber-800 hover:text-amber-900 underline" data-testid={`leg-${idx}-${kind}-retry`}>Retry</button>
      <a href={manualUrl} target="_blank" rel="noopener noreferrer"
         className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold inline-flex items-center gap-1"
         data-testid={`leg-${idx}-${kind}-manual`}>
        {manualLabel} <ExternalLink size={10} />
      </a>
    </div>
  );
}
